/**
 * Cookie consent helpers.
 * Analytics (GA4) must NOT load until the visitor explicitly accepts.
 * The stored value is a UI preference only — no personal data.
 */

export const CONSENT_STORAGE_KEY = "avt-cookie-consent";
export type ConsentValue = "accepted" | "rejected";

const GA_MEASUREMENT_ID = "G-4PRD59BLZ0";

export const getStoredConsent = (): ConsentValue | null => {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
};

export const setStoredConsent = (value: ConsentValue) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    /* storage unavailable — consent simply won't persist */
  }
};

export const hasAnalyticsConsent = () => getStoredConsent() === "accepted";

let analyticsLoaded = false;

/** Injects the GA4 script. Safe to call multiple times. */
export const loadAnalytics = () => {
  if (analyticsLoaded || typeof window === "undefined") return;
  if (document.getElementById("ga4-script")) {
    analyticsLoaded = true;
    return;
  }
  analyticsLoaded = true;

  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag as typeof window.gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    anonymize_ip: true,
    cookie_flags: "SameSite=None;Secure",
  });
};

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
